import { useCallback, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { FiArrowLeft } from 'react-icons/fi';
import { AppList } from '@/shared/app-list-layout';
import type { DataTableView } from '@/shared/app-list-layout';
import type { ListColumn } from '@/shared/app-list-layout/types';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Switch } from '@/shared/ui/switch';
import { ErrorState } from '@/shared/feedback';
import { toError } from '@/modules/ai/shared';
import { aiApi } from '@/modules/ai/services/ai-api';
import type { TenantAiAvailability } from '@/api/schemas/ai';

/**
 * Which tenants may use AI, and on whose money.
 *
 * The toggle here is the platform's: switching AI off for a tenant takes it
 * away from that tenant entirely, whatever it has configured for itself.
 *
 * The SECOND toggle is the platform's per-tenant grant: whether this tenant may
 * configure a provider of its own. It is a platform decision because the two
 * halves of that permission — the deployment-wide switch and this per-tenant
 * grant — are what decide whose credentials are spent. A tenant cannot grant
 * itself either half: the API refuses the member in the tenant's own body, and
 * the platform console is where it is set.
 *
 * A refusal is the shared surface's own state — `AppList` reads the 403 and
 * renders the denial, so this page does not branch on it.
 */

/** Saved views rendered as tabs above the tenant list. */
function buildAvailabilityViews(counts: Partial<Record<string, number>>): DataTableView[] {
  return [
    { id: 'all', label: 'All tenants', count: counts.all },
    { id: 'enabled', label: 'AI enabled', filters: { aiEnabled: 'true' }, count: counts.enabled },
    {
      id: 'disabled',
      label: 'AI disabled',
      filters: { aiEnabled: 'false' },
      count: counts.disabled,
    },
  ];
}

/**
 * The fields a search term is matched against.
 *
 * The slug is what an operator knows a tenant by, so it leads; the id is
 * included because a support ticket quotes the id, not the slug.
 */
function searchFields(row: TenantAiAvailability): ReadonlyArray<string | null | undefined> {
  return [row.tenantSlug, row.tenantStatus, row.tenantId];
}

export function AiTenantAvailabilityPage(): ReactNode {
  const navigate = useNavigate();
  const read = useCallback(() => aiApi.listPlatformTenantAvailability(), []);

  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<Error | null>(null);
  const [reload, setReload] = useState<(() => void) | null>(null);

  const columns = buildColumns({
    busyId,
    onToggle: (row, aiEnabled) => {
      void toggleTenantAi({ setBusyId, setActionError, reload }, row, aiEnabled);
    },
    onGrantOwnProvider: (row, allowTenantOwnedProvider) => {
      void grantTenantOwnProvider({ setBusyId, setActionError, reload }, row, allowTenantOwnedProvider);
    },
  });

  return (
    <div className="space-y-4">
      {actionError ? <ErrorState title="Action failed" message={actionError.message} /> : null}

      <AppList<TenantAiAvailability>
        title="Tenant AI availability"
        description="Which tenants may use AI, where their configuration comes from, and what they are allowed."
        read={read}
        searchFields={searchFields}
        countKeyOf={(row) => (row.aiEnabled ? 'enabled' : 'disabled')}
        buildViews={buildAvailabilityViews}
        columns={columns}
        getRowId={(row) => row.tenantId}
        // Seven columns run off a phone screen, so the page asks for the
        // responsive presentation: cards below `sm`, the table above.
        responsive
        onReloadReady={setReload}
        toolbarActions={
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              void navigate({ to: '/platform/ai' });
            }}
          >
            <FiArrowLeft size={14} aria-hidden="true" />
            Back to AI console
          </Button>
        }
        emptyTitle="No tenants yet"
        emptyDescription="This deployment has no tenants to configure."
        noMatchDescription="No tenant matches this tab, search or filter."
      />
    </div>
  );
}

type TenantAvailabilityContext = {
  setBusyId: (id: string | null) => void;
  setActionError: (error: Error | null) => void;
  reload: (() => void) | null;
};

async function toggleTenantAi(
  context: TenantAvailabilityContext,
  row: TenantAiAvailability,
  aiEnabled: boolean,
): Promise<void> {
  context.setBusyId(row.tenantId);
  context.setActionError(null);
  try {
    await aiApi.updatePlatformTenantAvailability(row.tenantId, { aiEnabled });
    context.reload?.();
  } catch (cause) {
    context.setActionError(toError(cause));
  } finally {
    context.setBusyId(null);
  }
}

/**
 * Grant or withhold the PER-TENANT permission to configure a provider of its own.
 *
 * The deployment-wide switch is the other half, and it lives on the console's deployment
 * settings. Both halves must be on before a tenant-owned configuration is used — which is
 * why this is the platform's write and not the tenant's.
 */
async function grantTenantOwnProvider(
  context: TenantAvailabilityContext,
  row: TenantAiAvailability,
  allowTenantOwnedProvider: boolean,
): Promise<void> {
  context.setBusyId(row.tenantId);
  context.setActionError(null);
  try {
    await aiApi.updatePlatformTenantAvailability(row.tenantId, { allowTenantOwnedProvider });
    context.reload?.();
  } catch (cause) {
    context.setActionError(toError(cause));
  } finally {
    context.setBusyId(null);
  }
}

type ColumnContext = {
  busyId: string | null;
  onToggle: (row: TenantAiAvailability, aiEnabled: boolean) => void;
  onGrantOwnProvider: (row: TenantAiAvailability, allowTenantOwnedProvider: boolean) => void;
};

function buildColumns(context: ColumnContext): ReadonlyArray<ListColumn<TenantAiAvailability>> {
  return [
    ...buildIdentityColumns(),
    ...buildGrantColumns(context),
    ...buildLimitColumns(),
  ];
}

/**
 * Who the tenant is and where its configuration comes from.
 *
 * These are READ-ONLY columns: the source is the tenant's own choice, and the provider is
 * the tenant's own business. The platform's decisions are the two switches in
 * {@link buildGrantColumns}.
 */
function buildIdentityColumns(): ReadonlyArray<ListColumn<TenantAiAvailability>> {
  return [
    {
      id: 'tenant',
      header: 'Tenant',
      primary: true,
      cell: (row) => (
        <div className="flex flex-col">
          <span className="font-medium text-content">{row.tenantSlug}</span>
          <span className="text-xs text-content-muted">
            {row.tenantStatus} · {row.tenantId}
          </span>
        </div>
      ),
    },
    {
      id: 'source',
      header: 'Configuration source',
      cell: (row) => (
        <Badge tone={row.configSource === 'tenant_owned' ? 'info' : 'neutral'}>
          {row.configSource === 'tenant_owned' ? 'Tenant-owned' : 'Platform default'}
        </Badge>
      ),
    },
    {
      id: 'provider',
      header: 'Tenant provider',
      cell: (row) => (
        <span className="text-xs text-content-muted">{row.tenantProviderId ?? '—'}</span>
      ),
    },
  ];
}

/**
 * The platform's two per-tenant decisions.
 *
 * `aiEnabled` says whether the tenant has AI at all. `allowTenantOwnedProvider` says whether
 * this tenant may bring a provider of its own — the per-tenant half of a permission whose
 * other half is the deployment-wide switch on the console's deployment settings.
 */
function buildGrantColumns(context: ColumnContext): ReadonlyArray<ListColumn<TenantAiAvailability>> {
  return [
    {
      id: 'enabled',
      header: 'AI',
      filter: {
        type: 'select',
        options: [
          { value: 'true', label: 'Enabled' },
          { value: 'false', label: 'Disabled' },
        ],
        placeholder: 'Any',
      },
      cell: (row) => (
        <Switch
          label={row.aiEnabled ? 'Enabled' : 'Disabled'}
          description={
            row.aiEnabled ? 'AI is available to this tenant' : 'AI is turned off for this tenant'
          }
          checked={row.aiEnabled}
          disabled={context.busyId === row.tenantId}
          onCheckedChange={(checked) => {
            context.onToggle(row, checked);
          }}
        />
      ),
    },
    {
      id: 'ownProvider',
      header: 'May own a provider',
      cell: (row) => (
        <Switch
          // The label STATES WHAT THE SWITCH IS, not what it currently is. A label that
          // flips with the state ('Allowed' / 'Not allowed') reads as the answer and
          // leaves the reader guessing what turning it on would mean — and it gives two
          // controls in one row two names that change underneath the reader.
          label="May own a provider"
          description={
            row.allowTenantOwnedProvider
              ? 'This tenant may configure a provider of its own'
              : 'This tenant uses the platform’s provider'
          }
          checked={row.allowTenantOwnedProvider}
          disabled={context.busyId === row.tenantId}
          onCheckedChange={(checked) => {
            context.onGrantOwnProvider(row, checked);
          }}
        />
      ),
    },
  ];
}

function buildLimitColumns(): ReadonlyArray<ListColumn<TenantAiAvailability>> {
  return [
    {
      id: 'requests',
      header: 'Monthly requests',
      cell: (row) => (
        <span>{row.monthlyRequestLimit === null ? 'No limit' : row.monthlyRequestLimit}</span>
      ),
    },
    {
      id: 'tokens',
      header: 'Monthly tokens',
      cell: (row) => (
        <span>{row.monthlyTokenLimit === null ? 'No limit' : row.monthlyTokenLimit}</span>
      ),
    },
    {
      id: 'fallback',
      header: 'Platform fallback',
      cell: (row) => (
        <Badge tone={row.allowPlatformFallback ? 'caution' : 'neutral'}>
          {row.allowPlatformFallback ? 'Allowed' : 'Not allowed'}
        </Badge>
      ),
    },
  ];
}
